/*
 * SPDX-FileCopyrightText: syuilo and misskey-project
 * SPDX-License-Identifier: AGPL-3.0-only
 */

import { watch, version as vueVersion } from 'vue';
import { compareVersions } from 'compare-versions';
import { version, lang, apiUrl } from '@@/js/config.js';
import defaultLightTheme from '@@/themes/l-light.json5';
import defaultDarkTheme from '@@/themes/d-green-lime.json5';
import { createGtag, addGtag, consent as gtagConsent } from 'vue-gtag';// FIXME Google Analytics 周りの機能のチェック
import type { App } from 'vue';
import type { GtagConsentParams } from '@/types/gtag';
import widgets from '@/widgets/index.js';
import directives from '@/directives/index.js';
import components from '@/components/index.js';
import { applyTheme } from '@/theme.js';
import { isDeviceDarkmode } from '@/utility/is-device-darkmode.js';
import { i18n } from '@/i18n.js';
import { refreshCurrentAccount, login, updateCurrentAccountPartial } from '@/accounts.js';
import { store } from '@/store.js';
import { fetchInstance, instance } from '@/instance.js';
import { deviceKind, updateDeviceKind } from '@/utility/device-kind.js';
import { reloadChannel } from '@/utility/unison-reload.js';
import { getUrlWithoutLoginId } from '@/utility/login-id.js';
import { getAccountFromId } from '@/utility/get-account-from-id.js';
import { misskeyApi } from '@/utility/misskey-api.js';
import { deckStore } from '@/ui/deck/deck-store.js';
import { miLocalStorage } from '@/local-storage.js';
import { fetchCustomEmojis } from '@/custom-emojis.js';
import { prefer } from '@/preferences.js';
import { sensitiveContentConsent } from '@/utility/sensitive-content-consent.js';
import { getDeviceId, setUserProperties } from '@/utility/tracking-user-properties.js';
import { $i, iAmAdmin } from '@/i.js';
import { mainRouter } from '@/router.js';
import { getAutoPostingLang, getDefaultViewingLangs } from '@/utility/posting-language.js';

export async function common(createVue: () => Promise<App<Element>>) {
	console.info(`Misskey v${version}`);

	if (_DEV_) {
		console.warn('Development mode!!!');

		console.info(`vue ${vueVersion}`);

		window.addEventListener('error', event => {
			console.error(event);
			/*
			alert({
				type: 'error',
				title: 'DEV: Unhandled error',
				text: event.message
			});
			*/
		});

		window.addEventListener('unhandledrejection', event => {
			console.error(event);
			/*
			alert({
				type: 'error',
				title: 'DEV: Unhandled promise rejection',
				text: event.reason
			});
			*/
		});
	}

	let isClientUpdated = false;

	//#region クライアントが更新されたかチェック
	const lastVersion = miLocalStorage.getItem('lastVersion');
	if (lastVersion !== version) {
		miLocalStorage.setItem('lastVersion', version);

		// テーマリビルドするため
		miLocalStorage.removeItem('theme');

		try { // 変なバージョン文字列来るとcompareVersionsでエラーになるため
			if (lastVersion != null && compareVersions(version, lastVersion) === 1) {
				isClientUpdated = true;
			}
		} catch (err) { /* empty */ }
	}
	//#endregion

	// タッチデバイスでCSSの:hoverを機能させる
	window.document.addEventListener('touchend', () => {}, { passive: true });

	// URLに#pswpを含む場合は取り除く
	if (window.location.hash === '#pswp') {
		window.history.replaceState(null, '', window.location.href.replace('#pswp', ''));
	}

	// URLに#pswpを含む場合は取り除く
	if (window.location.hash === '#pswp') {
		window.history.replaceState(null, '', window.location.href.replace('#pswp', ''));
	}

	// 一斉リロード
	reloadChannel.addEventListener('message', path => {
		if (path !== null) window.location.href = path;
		else window.location.reload();
	});

	// If mobile, insert the viewport meta tag
	if (['smartphone', 'tablet'].includes(deviceKind)) {
		const viewport = window.document.getElementsByName('viewport').item(0);
		viewport.setAttribute('content',
			`${viewport.getAttribute('content')}, minimum-scale=1, maximum-scale=1, user-scalable=no, viewport-fit=cover`);
	}

	//#region Set lang attr
	const html = window.document.documentElement;
	html.setAttribute('lang', lang);
	//#endregion

	await store.ready;
	await deckStore.ready;

	// チュートリアルを完了していない通常ユーザーの場合、チュートリアルにリダイレクト
	if (!instance.canSkipInitialTutorial && $i && !iAmAdmin && store.s.accountSetupWizard !== -1 && !window.location.pathname.startsWith('/onboarding') && !window.location.pathname.startsWith('/signup-complete')) {
		const param = new URLSearchParams();
		param.set('redirected_from', window.location.pathname + window.location.search + window.location.hash);
		window.location.replace('/onboarding?' + param.toString());
		await new Promise(() => {}); // never resolves; wait for navigation
	}

	if ($i) {
		const hasLanguageConfig = $i.postingLang != null || ($i.viewingLangs?.length ?? 0) > 0;
		if (!hasLanguageConfig) {
			const browserLanguage = typeof navigator === 'undefined' ? null : navigator.language;
			const autoPostingLang = getAutoPostingLang(browserLanguage);
			const defaultViewingLangs = getDefaultViewingLangs(autoPostingLang);
			try {
				const updated = await misskeyApi('i/update', {
					postingLang: autoPostingLang,
					viewingLangs: defaultViewingLangs,
				});
				updateCurrentAccountPartial({
					postingLang: updated.postingLang,
					viewingLangs: updated.viewingLangs,
					showMediaInAllLanguages: updated.showMediaInAllLanguages,
					showHashtagsInAllLanguages: updated.showHashtagsInAllLanguages,
				});
				miLocalStorage.setItem('postingLangAutoDetected', autoPostingLang);
				if (browserLanguage) {
					miLocalStorage.setItem('postingLangAutoDetectBase', browserLanguage);
				} else {
					miLocalStorage.removeItem('postingLangAutoDetectBase');
				}
			} catch (err) {
				console.warn('Failed to set default language config', err);
			}
		}
	}

	const fetchInstanceMetaPromise = fetchInstance();

	fetchInstanceMetaPromise.then(() => {
		miLocalStorage.setItem('v', instance.version);
	});

	const params = new URLSearchParams(window.location.search);
	//#region loginId
	const loginId = params.get('loginId');

	if (loginId) {
		const target = getUrlWithoutLoginId(window.location.href);

		if (!$i || $i.id !== loginId) {
			const account = await getAccountFromId(loginId);
			if (account) {
				await login(account.token, target);
			}
		}

		window.history.replaceState({ misskey: 'loginId' }, '', target);
	}
	//#endregion

	//#region kawaii
	if (params.has('kawaii') || params.has('uwu')) {
		const v = params.get('kawaii') ?? params.get('uwu');
		if (v === 'false' || v === '0' || v === 'no' || v === 'off') {
			miLocalStorage.removeItem('kawaii');
		} else {
			miLocalStorage.setItem('kawaii', 'true');
		}
	}
	//#endregion

	// NOTE: この処理は必ずクライアント更新チェック処理より後に来ること(テーマ再構築のため)
	watch(store.r.darkMode, (darkMode) => {
		applyTheme(darkMode
			? (prefer.s.darkTheme ?? defaultDarkTheme)
			: (prefer.s.lightTheme ?? defaultLightTheme),
		);
	}, { immediate: miLocalStorage.getItem('theme') == null });

	window.document.documentElement.dataset.colorScheme = store.s.darkMode ? 'dark' : 'light';

	const darkTheme = prefer.model('darkTheme');
	const lightTheme = prefer.model('lightTheme');

	watch(darkTheme, (theme) => {
		if (store.s.darkMode) {
			applyTheme(theme ?? defaultDarkTheme);
		}
	});

	watch(lightTheme, (theme) => {
		if (!store.s.darkMode) {
			applyTheme(theme ?? defaultLightTheme);
		}
	});

	//#region Sync dark mode
	if (prefer.s.syncDeviceDarkMode) {
		store.set('darkMode', isDeviceDarkmode());
	}

	window.matchMedia('(prefers-color-scheme: dark)').addEventListener('change', (mql) => {
		if (prefer.s.syncDeviceDarkMode) {
			store.set('darkMode', mql.matches);
		}
	});
	//#endregion

	if (prefer.s.darkTheme && store.s.darkMode) {
		if (miLocalStorage.getItem('themeId') !== prefer.s.darkTheme.id) applyTheme(prefer.s.darkTheme);
	} else if (prefer.s.lightTheme && !store.s.darkMode) {
		if (miLocalStorage.getItem('themeId') !== prefer.s.lightTheme.id) applyTheme(prefer.s.lightTheme);
	}

	fetchInstanceMetaPromise.then(() => {
		// TODO: instance.defaultLightTheme/instance.defaultDarkThemeが不正な形式だった場合のケア
		if (prefer.s.lightTheme == null && instance.defaultLightTheme != null) prefer.commit('lightTheme', JSON.parse(instance.defaultLightTheme));
		if (prefer.s.darkTheme == null && instance.defaultDarkTheme != null) prefer.commit('darkTheme', JSON.parse(instance.defaultDarkTheme));
	});

	watch(prefer.r.overridedDeviceKind, (kind) => {
		updateDeviceKind(kind);
	}, { immediate: true });

	watch(prefer.r.useBlurEffectForModal, v => {
		window.document.documentElement.style.setProperty('--MI-modalBgFilter', v ? 'blur(4px)' : 'none');
	}, { immediate: true });

	watch(prefer.r.useBlurEffect, v => {
		if (v) {
			window.document.documentElement.style.removeProperty('--MI-blur');
		} else {
			window.document.documentElement.style.setProperty('--MI-blur', 'none');
		}
	}, { immediate: true });

	// Keep screen on
	const onVisibilityChange = () => window.document.addEventListener('visibilitychange', () => {
		if (window.document.visibilityState === 'visible') {
			navigator.wakeLock.request('screen');
		}
	});
	if (prefer.s.keepScreenOn && 'wakeLock' in navigator) {
		navigator.wakeLock.request('screen')
			.then(onVisibilityChange)
			.catch(() => {
				// On WebKit-based browsers, user activation is required to send wake lock request
				// https://webkit.org/blog/13862/the-user-activation-api/
				window.document.addEventListener(
					'click',
					() => navigator.wakeLock.request('screen').then(onVisibilityChange),
					{ once: true },
				);
			});
	}

	if (prefer.s.makeEveryTextElementsSelectable) {
		window.document.documentElement.classList.add('forceSelectableAll');
	} else {
		// When global selection is disabled, clear lingering selections if the user clicks outside selectable areas.
		const clearSelectionOnPointerDown = (ev: PointerEvent) => {
			if (ev.button !== 0 && ev.pointerType !== 'touch' && ev.pointerType !== 'pen') return;

			const selection = window.getSelection();
			if (!selection || selection.isCollapsed) return;

			const path = typeof ev.composedPath === 'function' ? ev.composedPath() : [];
			let targetElement: Element | null = null;

			for (const item of path) {
				if (item instanceof Element) {
					targetElement = item;
					break;
				}
			}

			if (!targetElement) {
				const target = ev.target;
				if (target instanceof Element) {
					targetElement = target;
				} else if (target instanceof Node) {
					targetElement = target.parentElement;
				}
			}

			if (targetElement?.closest('textarea, input, [contenteditable]:not([contenteditable="false"]), ._selectable, ._selectableAtomic')) return;

			selection.removeAllRanges();
		};

		window.addEventListener('pointerdown', clearSelectionOnPointerDown, { capture: true });
	}

	//#region Fetch user
	if ($i && $i.token) {
		if (_DEV_) {
			console.log('account cache found. refreshing...');
		}

		refreshCurrentAccount();
	}
	//#endregion

	try {
		await fetchCustomEmojis();
	} catch (err) { /* empty */ }

	const app = await createVue();

	if (_DEV_) {
		app.config.performance = true;
	}

	widgets(app);
	directives(app);
	components(app);
	if (instance.googleAnalyticsId) {
		app.use(createGtag( {
			tagId: instance.googleAnalyticsId,
			config: {
				anonymize_ip: false,
				send_page_view: true,
			},
			pageTracker: {
				router: mainRouter,
				useScreenview: true,
			},
			initMode: 'manual',
			appName: `Misskey v${version}`,
		}));

		const gtagConsentParams = miLocalStorage.getItemAsJson('gtagConsent') as GtagConsentParams ?? {
			ad_storage: 'denied',
			ad_user_data: 'denied',
			ad_personalization: 'denied',
			analytics_storage: 'denied',
			functionality_storage: 'denied',
			personalization_storage: 'denied',
			security_storage: 'granted',
		};
		miLocalStorage.setItemAsJson('gtagConsent', gtagConsentParams);
		gtagConsent('default', gtagConsentParams);

		if (miLocalStorage.getItem('gaConsent') === 'true') {
			// noinspection ES6MissingAwait
			addGtag();
		}
	}

	// https://github.com/misskey-dev/misskey/pull/8575#issuecomment-1114239210
	// なぜか2回実行されることがあるため、mountするdivを1つに制限する
	const rootEl = ((): HTMLElement => {
		const MISSKEY_MOUNT_DIV_ID = 'misskey_app';

		const currentRoot = window.document.getElementById(MISSKEY_MOUNT_DIV_ID);

		if (currentRoot) {
			console.warn('multiple import detected');
			return currentRoot;
		}

		const root = window.document.createElement('div');
		root.id = MISSKEY_MOUNT_DIV_ID;
		window.document.body.appendChild(root);
		return root;
	})();

	if (instance.sentryForFrontend) {
		const Sentry = await import('@sentry/vue');

		Sentry.init({
			app,
			release: version,
			integrations: [
				...(instance.sentryForFrontend.vueIntegration !== undefined ? [
					Sentry.vueIntegration(instance.sentryForFrontend.vueIntegration ?? undefined),
				] : []),
				...(instance.sentryForFrontend.browserTracingIntegration !== undefined ? [
					Sentry.browserTracingIntegration(instance.sentryForFrontend.browserTracingIntegration ?? undefined),
				] : []),
				...(instance.sentryForFrontend.replayIntegration !== undefined ? [
					Sentry.replayIntegration(instance.sentryForFrontend.replayIntegration ?? undefined),
				] : []),
			],

			// Set tracesSampleRate to 1.0 to capture 100%
			tracesSampleRate: 1.0,

			// Set `tracePropagationTargets` to control for which URLs distributed tracing should be enabled
			...(instance.sentryForFrontend.browserTracingIntegration !== undefined ? {
				tracePropagationTargets: [apiUrl],
			} : {}),

			// Capture Replay for 10% of all sessions,
			// plus for 100% of sessions with an error
			...(instance.sentryForFrontend.replayIntegration !== undefined ? {
				replaysSessionSampleRate: 0.1,
				replaysOnErrorSampleRate: 1.0,
			} : {}),

			...instance.sentryForFrontend.options,
		});
	}

	if (instance.sentryForFrontend || instance.googleAnalyticsId) {
		const consentValue = sensitiveContentConsent.value === null ? 'unset' : String(sensitiveContentConsent.value);
		setUserProperties({
			deviceId: getDeviceId(),
			sensitiveContentConsent: consentValue,
			displayOfSensitiveAds: String(prefer.s.displayOfSensitiveAds),
		});
	}

	app.mount(rootEl);

	// boot.jsのやつを解除
	window.onerror = null;
	window.onunhandledrejection = null;

	removeSplash();

	//#region Self-XSS 対策メッセージ
	if (!_DEV_) {
		console.log(
			`%c${i18n.ts._selfXssPrevention.warning}`,
			'color: #f00; background-color: #ff0; font-size: 36px; padding: 4px;',
		);
		console.log(
			`%c${i18n.ts._selfXssPrevention.title}`,
			'color: #f00; font-weight: 900; font-family: "Hiragino Sans W9", "Hiragino Kaku Gothic ProN", sans-serif; font-size: 24px;',
		);
		console.log(
			`%c${i18n.ts._selfXssPrevention.description1}`,
			'font-size: 16px; font-weight: 700;',
		);
		console.log(
			`%c${i18n.ts._selfXssPrevention.description2}`,
			'font-size: 16px;',
			'font-size: 20px; font-weight: 700; color: #f00;',
		);
		console.log(i18n.tsx._selfXssPrevention.description3({ link: 'https://misskey-hub.net/docs/for-users/resources/self-xss/' }));
	}
	//#endregion

	return {
		isClientUpdated,
		lastVersion,
		app,
	};
}

function removeSplash() {
	const splash = window.document.getElementById('splash');
	if (splash) {
		splash.style.opacity = '0';
		splash.style.pointerEvents = 'none';

		// transitionendイベントが発火しない場合があるため
		window.setTimeout(() => {
			splash.remove();
		}, 1000);
	}
}
